import { useState, useEffect } from "react";
import { api } from "../api/api";

const HOF = { lat: 52.4089, lon: 10.8601 };

function sprich(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "de-DE";
  u.rate = 0.95;
  window.speechSynthesis.speak(u);
}

function bautAnsage(l, index) {
  let text = `Stopp ${index}: ${l.nachname}, ${l.strasse} ${l.hausnummer}. `;
  text += `${l.menge} ${l.einheit} ${l.produktName} liefern. `;
  if (l.flaschenBeimKunden > 0) text += `${l.flaschenBeimKunden} Flasche${l.flaschenBeimKunden !== 1 ? "n" : ""} abholen. `;
  if (l.notiz) text += `Notiz: ${l.notiz}.`;
  return text;
}

export default function FahrerKarte({ lieferungen, tourFarbe = "#e65100" }) {
  const [geliefert,      setGeliefert]      = useState({});
  const [leergutEingabe, setLeergutEingabe] = useState({});
  const [leergutFertig,  setLeergutFertig]  = useState({});
  const [aktiverStopp,   setAktiverStopp]   = useState(0);
  const [loadingId,      setLoadingId]      = useState(null);

  // Lieferstatus aus DB-Daten initialisieren sobald lieferungen geladen sind
  useEffect(() => {
    if (!lieferungen.length) return;
    const gel = {}, eingabe = {}, fertig = {};
    lieferungen.forEach(l => {
      if (!l.istGeliefert) return;
      gel[l.bestellungId] = { istMehrweg: l.istMehrweg, flaschenNachLieferung: l.flaschenBeimKunden };
      if (l.istMehrweg) {
        eingabe[l.bestellungId] = String(l.leergutAbgeholt ?? l.flaschenBeimKunden);
        if (l.leergutAbgeholt !== null && l.leergutAbgeholt !== undefined)
          fertig[l.bestellungId] = l.leergutAbgeholt;
      }
    });
    setGeliefert(prev => ({ ...prev, ...gel }));
    setLeergutEingabe(prev => ({ ...prev, ...eingabe }));
    setLeergutFertig(prev => ({ ...prev, ...fertig }));
  }, [lieferungen]);

  const handleGeliefert = async (bestellungId) => {
    setLoadingId(bestellungId);
    try {
      const res = await api.fahrer.geliefert(bestellungId);
      setGeliefert(prev => ({ ...prev, [bestellungId]: res }));
      if (res.istMehrweg) {
        // Vorausfüllen mit dem neuen Stand (alle Flaschen beim Kunden)
        setLeergutEingabe(prev => ({ ...prev, [bestellungId]: String(res.flaschenNachLieferung) }));
      }
    } finally {
      setLoadingId(null);
    }
  };

  const handleLeergut = async (bestellungId) => {
    const abgeholt = parseInt(leergutEingabe[bestellungId]) || 0;
    setLoadingId(`leergut-${bestellungId}`);
    try {
      await api.fahrer.leergut(bestellungId, abgeholt);
      setLeergutFertig(prev => ({ ...prev, [bestellungId]: abgeholt }));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div>
      {/* Infobar */}
      {lieferungen.length > 0 && (
        <div className="card" style={{ background: "#fff3e0", border: "1.5px solid #ffcc80", marginBottom: "1rem", display: "flex", gap: "1rem", padding: "0.75rem 1.25rem", flexWrap: "wrap", alignItems: "center" }}>
          <span>📦 <strong>{lieferungen.length} Stopps</strong></span>

          <button
            onClick={() => {
              const l = lieferungen[aktiverStopp];
              if (!l) return;
              sprich(bautAnsage(l, aktiverStopp + 1));
              setAktiverStopp(prev => Math.min(prev + 1, lieferungen.length - 1));
            }}
            style={{ background: "#1565c0", color: "white", border: "none", borderRadius: "8px", padding: "0.3rem 0.9rem", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap" }}>
            🔊 Stopp {aktiverStopp + 1} vorlesen
          </button>
          {aktiverStopp > 0 && (
            <button onClick={() => setAktiverStopp(0)}
              style={{ background: "#f5f5f5", color: "#555", border: "1px solid #ddd", borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.82rem", cursor: "pointer" }}>
              ↺ Reset
            </button>
          )}

          <a
            href={(() => {
              const origin = `${HOF.lat},${HOF.lon}`;
              const stops  = lieferungen.map(l => encodeURIComponent(`${l.strasse} ${l.hausnummer}, ${l.plz} ${l.ort}, Deutschland`));
              const dest   = stops[stops.length - 1];
              const waypoints = stops.slice(0, -1).join("|");
              return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${waypoints ? `&waypoints=${waypoints}` : ""}&travelmode=driving`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: "#4285f4", color: "white", padding: "0.3rem 0.8rem", borderRadius: "8px", textDecoration: "none", fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
            🗺️ In Google Maps öffnen
          </a>

          {/* Gesamtmenge pro Produkt */}
          {(() => {
            const summen = {};
            lieferungen.forEach(l => {
              const key = l.produktName ?? "Unbekannt";
              summen[key] = (summen[key] ?? 0) + l.menge;
            });
            return Object.entries(summen).map(([name, menge]) => (
              <span key={name}>🛒 <strong>{menge} {lieferungen.find(l => l.produktName === name)?.einheit ?? ""} {name}</strong> mitnehmen</span>
            ));
          })()}

          {lieferungen.some(l => l.flaschenBeimKunden > 0) && (
            <span>🍼 <strong>{lieferungen.reduce((s, l) => s + (l.flaschenBeimKunden ?? 0), 0)} Flaschen</strong> gesamt bei Kunden</span>
          )}
        </div>
      )}

      {/* Stoppliste */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {lieferungen.length === 0
          ? <div className="card" style={{ textAlign: "center", color: "#aaa" }}>Keine Lieferungen.</div>
          : <>
            <div className="card" style={{ padding: "0.6rem 0.9rem", background: "#e8f5e9", border: "1.5px solid #a5d6a7" }}>
              <strong style={{ color: "#2e7d32" }}>🏠 Start: Milchhof Gehrke</strong>
            </div>
            {lieferungen.map((l, i) => {
              const g = geliefert[l.bestellungId];
              const istGeliefert = !!g;
              const istMehrweg   = l.istMehrweg;
              const leergutBest  = leergutFertig[l.bestellungId];
              const isLoading    = loadingId === l.bestellungId;
              const isLeergutLoading = loadingId === `leergut-${l.bestellungId}`;

              return (
                <div key={l.bestellungId} className="card" style={{ padding: "0.6rem 0.9rem", border: `1.5px solid ${istGeliefert ? "#a5d6a7" : "#ffe0b2"}`, background: istGeliefert ? "#f1f8f1" : "white" }}>
                  <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                    <div style={{ background: istGeliefert ? "#2e7d32" : tourFarbe, color: "white", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.85rem", flexShrink: 0 }}>
                      {istGeliefert ? "✓" : i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong>{l.vorname} {l.nachname}</strong>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${l.strasse} ${l.hausnummer}, ${l.plz} ${l.ort}, Deutschland`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", padding: "0 0.2rem", textDecoration: "none" }}
                            title="In Google Maps öffnen">📍</a>
                          <button onClick={() => sprich(bautAnsage(l, i + 1))}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", padding: "0 0.2rem" }}
                            title="Vorlesen">🔊</button>
                        </div>
                      </div>
                      {l.telefon && <span style={{ color: "#888", fontSize: "0.8rem" }}>📞 {l.telefon}</span>}
                      <div style={{ color: "#555", fontSize: "0.85rem" }}>{l.strasse} {l.hausnummer}, {l.plz} {l.ort}</div>
                      <div style={{ color: "#e65100", fontSize: "0.85rem", marginTop: "0.2rem" }}>
                        🛒 <strong>{l.menge} {l.einheit}</strong> {l.produktName} liefern
                        {istMehrweg
                          ? <span style={{ marginLeft: "0.4rem", background: "#e8f5e9", color: "#2e7d32", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.75rem", fontWeight: 700 }}>Mehrweg</span>
                          : <span style={{ marginLeft: "0.4rem", background: "#f5f5f5", color: "#888", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.75rem" }}>Einweg</span>
                        }
                      </div>
                      {istMehrweg && l.flaschenBeimKunden > 0 && !istGeliefert && (
                        <div style={{ color: "#1565c0", fontSize: "0.85rem", marginTop: "0.2rem" }}>
                          🍼 Aktuell <strong>{l.flaschenBeimKunden} Flasche{l.flaschenBeimKunden !== 1 ? "n" : ""}</strong> beim Kunden
                        </div>
                      )}
                      {l.notiz && <div style={{ color: "#555", fontSize: "0.8rem", marginTop: "0.2rem", fontStyle: "italic" }}>📝 {l.notiz}</div>}

                      <div style={{ marginTop: "0.6rem" }}>
                        {!istGeliefert ? (
                          /* ── Schritt 1: Geliefert ── */
                          <button
                            disabled={isLoading}
                            onClick={() => handleGeliefert(l.bestellungId)}
                            style={{ padding: "0.35rem 1rem", fontSize: "0.85rem", background: "#e8f5e9", color: "#2e7d32", border: "1.5px solid #a5d6a7", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}>
                            {isLoading ? "…" : "✓ Zugestellt"}
                          </button>
                        ) : istMehrweg && leergutBest === undefined ? (
                          /* ── Schritt 2: Leergut eintragen (nur Mehrweg) ── */
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", background: "#e3f2fd", border: "1.5px solid #90caf9", borderRadius: "8px", padding: "0.5rem 0.75rem" }}>
                            <span style={{ fontSize: "0.85rem", color: "#1565c0", fontWeight: 600 }}>🍼 Leergut mitgenommen:</span>
                            <input
                              type="number"
                              min="0"
                              value={leergutEingabe[l.bestellungId] ?? ""}
                              onChange={e => setLeergutEingabe(prev => ({ ...prev, [l.bestellungId]: e.target.value }))}
                              style={{ width: "60px", padding: "0.3rem 0.4rem", borderRadius: "6px", border: "1.5px solid #90caf9", fontSize: "0.9rem", fontWeight: 600, textAlign: "center" }}
                            />
                            <span style={{ fontSize: "0.82rem", color: "#555" }}>Flaschen</span>
                            <button
                              disabled={isLeergutLoading}
                              onClick={() => handleLeergut(l.bestellungId)}
                              style={{ padding: "0.3rem 0.8rem", fontSize: "0.85rem", background: "#1565c0", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}>
                              {isLeergutLoading ? "…" : "Bestätigen"}
                            </button>
                            <button
                              disabled={isLeergutLoading}
                              onClick={async () => {
                                setLeergutEingabe(prev => ({ ...prev, [l.bestellungId]: "0" }));
                                setLoadingId(`leergut-${l.bestellungId}`);
                                try {
                                  await api.fahrer.leergut(l.bestellungId, 0);
                                  setLeergutFertig(prev => ({ ...prev, [l.bestellungId]: 0 }));
                                } finally {
                                  setLoadingId(null);
                                }
                              }}
                              style={{ padding: "0.3rem 0.6rem", fontSize: "0.82rem", background: "none", border: "1px solid #90caf9", borderRadius: "6px", cursor: "pointer", color: "#555" }}>
                              Keine abgeholt
                            </button>
                          </div>
                        ) : (
                          /* ── Fertig ── */
                          <div style={{ fontSize: "0.85rem", color: "#2e7d32", fontWeight: 600 }}>
                            ✓ Zugestellt
                            {istMehrweg && leergutBest !== undefined && (
                              <span style={{ marginLeft: "0.75rem", color: "#1565c0" }}>
                                · 🍼 {leergutBest} Flasche{leergutBest !== 1 ? "n" : ""} abgeholt
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        }
      </div>
    </div>
  );
}
